import { useContext } from 'react'

import {
  Box, Button, Form, LoadingModule, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import useTrackingLink, { useTrackingLinkForm } from '@hooks/useTrackingLink'
import useTrackingLinks from '@hooks/useTrackingLinks'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import TrackingLinkListItem from '@components/TrackingLinkListItem'

const MODAL_KEY = 'CreateOrEditTrackingLinkModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTrackingLink: (payload: HandleCallbackActionParams) => void,
  deleteTrackingLink: (payload: HandleCallbackActionParams) => void,
  updateTrackingLink: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditTrackingLinkModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, createTrackingLink, deleteTrackingLink, updateTrackingLink,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const trackingLink = modalPayload?.trackingLink ?? {}
  const { recipient_id, recipient_type } = trackingLink

  const newRecord = !trackingLink.id

  const {
    callbacks: {
      createTrackingLink: createFn,
      updateTrackingLink: updateFn,
    },
    creating,
    updating,
  } = useTrackingLink(trackingLink)

  const {
    entityState,
    entityState: {
      title,
      url,
    },
    errors,
    handlers,
    saveEnabled,
  } = useTrackingLinkForm(trackingLink, { validateOn: 'change' })

  const trackingLinksPayload = useTrackingLinks({
    filters: {
      recipient_id,
      recipient_type,
    },
    performHttpRequests: !!recipient_type && !!recipient_id,
  })

  const {
    filteredTrackingLinks,
    hasFilteredTrackingLinks,
    loading,
  } = trackingLinksPayload

  // To be used with handleCallbackAction
  const createOrUpdateTrackingLinkPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Link ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Link" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Url" errorMessage={errors.url} marginTop="large">
            <input name="url" type="text" value={url} {...handlers} />
          </Form.Field>
        </Form>

        <LoadingModule boxProps={{ marginTop: 'large' }} loading={loading} />

        {!loading && hasFilteredTrackingLinks && (
          <>
            <SectionDivider marginTop="large">Current Links</SectionDivider>

            <Box flexDirection="column">
              {filteredTrackingLinks.map(link => (
                <TrackingLinkListItem
                  boxProps={{ paddingX: 0 }}
                  callbacks={{ deleteTrackingLink }}
                  key={link.id}
                  trackingLink={link}
                />
              ))}
            </Box>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createTrackingLink(createOrUpdateTrackingLinkPayload)
              : () => updateTrackingLink(createOrUpdateTrackingLinkPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Link` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTrackingLinkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
